import React, { useCallback, useEffect } from 'react';
import {
  Button,
  Input,
  Label,
  Tab,
  Text,
  TabList,
} from '@fluentui/react-components';
import { DatePicker } from '@fluentui/react-datepicker-compat';
import { VNotificationCategoryEnum, VNotificationTypeEnum } from '../../../services';
import SimpleInfoCard from '../../../components/simpleInfoCard';
import BetterDropdown from '../../../components/betterDropdown';
import { getCurrentDatePlus, timeDiffToNowSeconds } from '../../../common/utils';
import BetterIcon, { BetterIconName } from '../../../components/betterIcon';
import appConfig from '../../../config.app';
import { useAppDispatch } from '../../../hook';
import { snoozeTank } from '../_slice';

const durationTypes = [
  { id: '1h', label: '1 hour', minutes: 60 },
  { id: '2h', label: '2 hours', minutes: 120 },
  { id: '3h', label: '3 hours', minutes: 180 },
  { id: '4h', label: '4 hours', minutes: 240 },
  { id: '5h', label: '5 hours', minutes: 300 },
  { id: '6h', label: '6 hours', minutes: 360 },
  { id: '7h', label: '7 hours', minutes: 420 },
  { id: '8h', label: '8 hours', minutes: 480 },
  { id: '9h', label: '9 hours', minutes: 540 },
  { id: '10h', label: '10 hours', minutes: 600 },
  { id: '11h', label: '11 hours', minutes: 660 },
  { id: '12h', label: '12 hours', minutes: 720 },
  { id: '13h', label: '13 hours', minutes: 780 },
  { id: '14h', label: '14 hours', minutes: 840 },
  { id: '15h', label: '15 hours', minutes: 900 },
  { id: '16h', label: '16 hours', minutes: 960 },
  { id: 'custom', label: 'Custom' },
];

const hoursOptions = [
  { id: '00', label: '00' },
  { id: '01', label: '01' },
  { id: '02', label: '02' },
  { id: '03', label: '03' },
  { id: '04', label: '04' },
  { id: '05', label: '05' },
  { id: '06', label: '06' },
  { id: '07', label: '07' },
  { id: '08', label: '08' },
  { id: '09', label: '09' },
  { id: '10', label: '10' },
  { id: '11', label: '11' },
  { id: '12', label: '12' },
  { id: '13', label: '13' },
  { id: '14', label: '14' },
  { id: '15', label: '15' },
  { id: '16', label: '16' },
  { id: '17', label: '17' },
  { id: '18', label: '18' },
  { id: '19', label: '19' },
  { id: '20', label: '20' },
  { id: '21', label: '21' },
  { id: '22', label: '22' },
  { id: '23', label: '23' },
];

const minutesOptions = [
  { id: '00', label: '00' },
  { id: '05', label: '05' },
  { id: '10', label: '10' },
  { id: '15', label: '15' },
  { id: '20', label: '20' },
  { id: '25', label: '25' },
  { id: '30', label: '30' },
  { id: '35', label: '35' },
  { id: '40', label: '40' },
  { id: '45', label: '45' },
  { id: '50', label: '50' },
  { id: '55', label: '55' },
];

const SnoozeDialog: React.FC<{
  tankId: string,
  tankName: string,
  caddySerial?: string,
  description?: string,
  notificationCategory: VNotificationCategoryEnum,
  notificationType: VNotificationTypeEnum,
  onCancel: () => void,
  onConfirm: () => void,
}> = ({
  tankId,
  tankName,
  caddySerial,
  description,
  notificationCategory,
  notificationType,
  onCancel,
  onConfirm,
}) => {
  const dispatch = useAppDispatch();
  const [snoozeType, setSnoozeType] = React.useState<string>('duration'); // Snooze Duration or Snooze Until
  const [snoozeDurationType, setSnoozeDurationType] = React.useState<string>(''); // Duration Options or Custom
  const [snoozeDate, setSnoozeDate] = React.useState<Date>();
  const [snoozeHour, setSnoozeHour] = React.useState<string>(hoursOptions[0].id);
  const [snoozeMinute, setSnoozeMinute] = React.useState<string>(minutesOptions[0].id);
  const [snoozeUntil, setSnoozeUntil] = React.useState<Date>();
  const [snoozeDurationInfo, setSnoozeDurationInfo] = React.useState<string>('');
  const [note, setNote] = React.useState<string>('');

  const changeSnoozeType = (type: string) => {
    setSnoozeType(type);
    setSnoozeDurationType(durationTypes[0].id);
    setSnoozeDate(undefined);
    setSnoozeHour(hoursOptions[0].id);
    setSnoozeMinute(minutesOptions[0].id);
    setSnoozeUntil(undefined);
    setSnoozeDurationInfo('');
  };

  const updateSnoozeUntilForCustomDurationChange = useCallback((durationHours: string) => {
    const minutes = parseFloat(durationHours) * 60;
    if (!Number.isNaN(minutes) && minutes > 0) {
      setSnoozeUntil(getCurrentDatePlus(minutes * 60).toJSDate());
    } else {
      setSnoozeUntil(undefined);
    }
  }, []);

  const updateSnoozeUntilForDurationTypeChange = useCallback(() => {
    if (snoozeDurationType === '') return;
    if (snoozeDurationType !== 'custom') {
      const minutes = durationTypes.find((d) => d.id === snoozeDurationType)?.minutes || 0;
      setSnoozeUntil(getCurrentDatePlus(minutes * 60).toJSDate());
    } else {
      updateSnoozeUntilForCustomDurationChange('');
    }
  }, [snoozeDurationType, updateSnoozeUntilForCustomDurationChange]);

  const changeSnoozeDate = (date?: Date | null) => {
    setSnoozeDate(date || undefined);
  };

  const changeSnoozeHour = (hour: string) => {
    setSnoozeHour(hour);
  };

  const changeSnoozeMinute = (minute: string) => {
    setSnoozeMinute(minute);
  };

  useEffect(() => {
    setSnoozeDurationType(durationTypes[0].id);
  }, []);

  useEffect(() => {
    updateSnoozeUntilForDurationTypeChange();
  }, [snoozeDurationType, updateSnoozeUntilForDurationTypeChange]);

  useEffect(() => {
    const updateSnoozeUntil = () => {
      if (snoozeDate) {
        const newDate = new Date(snoozeDate);
        newDate.setHours(parseInt(snoozeHour, 10));
        newDate.setMinutes(parseInt(snoozeMinute, 10));
        newDate.setSeconds(0);
        newDate.setMilliseconds(0);
        const durationInHours = (newDate.getTime() - new Date().getTime()) / (1000 * 60 * 60);
        if (durationInHours > 0) {
          const hours = Math.floor(durationInHours);
          const minutes = Math.floor((durationInHours - hours) * 60);
          setSnoozeDurationInfo(`${hours}h ${minutes}m`);
          setSnoozeUntil(newDate);
          return;
        }
      }
      setSnoozeDurationInfo('');
      setSnoozeUntil(undefined);
    };
    updateSnoozeUntil();
  }, [snoozeDate, snoozeHour, snoozeMinute]);

  const snooze = async () => {
    // Date to string
    const snoozeUntilStr = snoozeUntil ? snoozeUntil.toISOString() : undefined;
    if (snoozeUntilStr && note) {
      await dispatch(snoozeTank({
        tankId,
        notificationType,
        snoozeUntil: snoozeUntilStr,
        note,
      }));
    }
    onConfirm();
  };

  const infoItems = [{ label: 'Tank Name', value: tankName }];
  if (caddySerial) {
    infoItems.push({ label: 'Caddy Serial', value: caddySerial });
  }
  if (description) {
    infoItems.push({ label: 'Description', value: description });
  }

  const isSnoozeUntilExceeded = snoozeUntil
    ? timeDiffToNowSeconds(snoozeUntil) > appConfig.maxSnoozeDurationHours * 3600
    : false;

  return (
    <div style={{
      display: 'flex',
      flexDirection: 'column',
      gap: 10,
      width: '98%',
      maxWidth: '600px',
      backgroundColor: 'white',
      borderRadius: 8,
      padding: 20,
      justifyContent: 'center',
      alignItems: 'center',
    }}>
      {/* Title */}
      <div style={{
        fontSize: 18,
        fontWeight: 'bold',
        width: '100%',
        display: 'flex',
        alignItems: 'center',
        gap: 10,
      }}>
        <BetterIcon
          name={ appConfig.eventCategories[notificationCategory]?.icon as BetterIconName }
          color={ appConfig.eventCategories[notificationCategory]?.color }
        />
        {notificationType}
      </div>
      {/* Notification details */}
      <div style={{
        width: '100%',
      }}>
        <SimpleInfoCard
          items={infoItems}
          defaultShowCount={15}
        />
      </div>
      {/* Tabs for snooze options */}
      <div style={{
        width: '100%',
      }}>
        <TabList
          selectedValue={snoozeType}
          onTabSelect={(_, d) => changeSnoozeType(d.value as string)}
        >
          <Tab value="duration"><Text size={400} weight='bold'>Snooze Duration</Text></Tab>
          <Tab value="until"><Text size={400} weight='bold'>Snooze Until</Text></Tab>
        </TabList>
      </div>
      <div style={{
        width: '100%',
        padding: 20,
        display: 'flex',
        alignItems: 'center',
        gap: 10,
      }}>
        {
          // Snooze options based on selected tab
          snoozeType === 'duration' && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                <BetterDropdown
                  style={{ width: 140, minWidth: 0 }}
                  options={durationTypes}
                  selectedOptionId={snoozeDurationType}
                  onOptionSelect={(x) => setSnoozeDurationType(x)}
                />
                {snoozeDurationType === 'custom' && (
                  <div>
                    <Input
                      style={{ width: 140 }}
                      type="number"
                      appearance='underline'
                      placeholder="Enter duration"
                      onChange={(e) => updateSnoozeUntilForCustomDurationChange(e.target.value)}
                      min={0}
                    />
                    hours
                  </div>
                )}
              </div>
              {
                snoozeUntil
                  && <>
                    <div>The alarm will be snoozed until {snoozeUntil.toLocaleString()}</div>
                    {
                      isSnoozeUntilExceeded
                        && <div style={{ color: 'red' }}>
                          The snooze duration exceeds the maximum allowed of {
                            appConfig.maxSnoozeDurationHours
                          } hours.
                        </div>
                    }
                  </>
              }
            </div>
          )
        }
        {
          // Snooze until date and time picker
          snoozeType === 'until' && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                <DatePicker
                  allowTextInput
                  placeholder="Select a date..."
                  minDate={new Date()}
                  style={{ width: 170 }}
                  onSelectDate={(date) => changeSnoozeDate(date)}
                />
                <BetterDropdown
                  style={{ width: 60, minWidth: 0 }}
                  options={hoursOptions}
                  selectedOptionId={snoozeHour}
                  onOptionSelect={(x) => changeSnoozeHour(x)}
                /> :
                <BetterDropdown
                  style={{ width: 60, minWidth: 0 }}
                  options={minutesOptions}
                  selectedOptionId={snoozeMinute}
                  onOptionSelect={(x) => changeSnoozeMinute(x)}
                />
              </div>
              {
                snoozeUntil && snoozeDurationInfo !== ''
                  && <>
                    <div>The alarm will be snoozed for {snoozeDurationInfo}</div>
                    {
                      isSnoozeUntilExceeded
                        && <div style={{ color: 'red' }}>
                          The snooze duration exceeds the maximum allowed of {
                            appConfig.maxSnoozeDurationHours
                          } hours.
                        </div>
                    }
                  </>
              }
            </div>
          )
        }
      </div>
      {/* Note */}
      <div style={{
        width: '100%',
        paddingLeft: 10,
        paddingRight: 10,
      }}>
        <Label
          htmlFor="note"
          size='medium'
          weight='semibold'
          style={{ display: 'flex', fontWeight: 'bold' }}
        >
          Note
        </Label>
        <Input
          type="text"
          id="note"
          size='large'
          appearance='underline'
          style={{ width: '100%' }}
          placeholder="Enter your note"
          disabled={false}
          readOnly={false}
          maxLength={200}
          onChange={(e) => setNote(e.target.value.trim())}
        />
      </div>
      {/* Information */}
      <div style={{
        width: '100%',
        padding: 10,
      }}>
        <div>
          Snoozing will silence this specific alarm until the set time.
          Additional alarms for this and other caddies will still come through.
        </div>
        <div>
          It will take a couple of minutes for the changes to be applied to the caddy.
        </div>
      </div>
      {/* Actions */}
      <div style={{
        display: 'flex',
        gap: 10,
      }}>
        <Button onClick={onCancel}>Cancel</Button>
        <Button
          appearance='primary'
          onClick={snooze}
          disabled={snoozeUntil === undefined || note === '' || isSnoozeUntilExceeded}
        >Snooze</Button>
      </div>
    </div>
  );
};

export default SnoozeDialog;
